// StripeEmbeddedCheckout.jsx
import React, { useEffect, useRef } from 'react';
import { loadStripe } from '@stripe/stripe-js';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

// Module-level singleton to prevent multiple instances
let globalCheckoutInstance = null;
let globalClientSecret = null;
let initializationPromise = null;

// Function to clear global state (can be called externally)
function clearGlobalStripeCheckout() {
  console.log('StripeEmbeddedCheckout: Clearing global state');
  if (globalCheckoutInstance) {
    try {
      globalCheckoutInstance.destroy();
    } catch (e) {
      console.warn('StripeEmbeddedCheckout: Error destroying global instance during clear', e);
    }
  }
  globalCheckoutInstance = null;
  globalClientSecret = null;
  initializationPromise = null;
  console.log('StripeEmbeddedCheckout: Global state cleared');
}

export default function StripeEmbeddedCheckout({ clientSecret, onComplete }) {
  const containerRef = useRef(null);
  const checkoutRef = useRef(null);
  const completedRef = useRef(false);
  const onCompleteRef = useRef(onComplete);

  // Keep onComplete ref updated without triggering re-renders
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  // Clear global state when clientSecret becomes null (payment completed/cleared)
  useEffect(() => {
    if (!clientSecret && globalCheckoutInstance) {
      console.log('StripeEmbeddedCheckout: clientSecret cleared, cleaning up global state');
      clearGlobalStripeCheckout();
    }
  }, [clientSecret]);

  useEffect(() => {
    if (!clientSecret || !containerRef.current) {
      return;
    }

    // If we already have an instance for this EXACT clientSecret, skip
    if (globalClientSecret === clientSecret && globalCheckoutInstance) {
      // If this component doesn't own the instance, try to mount it here
      if (globalCheckoutInstance !== checkoutRef.current) {
        console.log('StripeEmbeddedCheckout: Reusing existing instance');
        checkoutRef.current = globalCheckoutInstance;
        // Try to remount if needed
        try {
          if (containerRef.current && !containerRef.current.hasChildNodes()) {
            globalCheckoutInstance.mount(containerRef.current);
          }
        } catch (e) {
          console.warn('StripeEmbeddedCheckout: Could not remount existing instance', e);
        }
      }
      return;
    }

    // If we have an instance but with a DIFFERENT clientSecret, destroy it first
    if (globalCheckoutInstance && globalClientSecret !== clientSecret) {
      console.log('StripeEmbeddedCheckout: Different clientSecret detected, destroying old instance');
      clearGlobalStripeCheckout();
    }

    // If initialization is in progress for this EXACT clientSecret, wait for it
    if (initializationPromise && globalClientSecret === clientSecret) {
      console.log('StripeEmbeddedCheckout: Waiting for existing initialization...');
      initializationPromise.then(() => {
        if (globalCheckoutInstance && globalClientSecret === clientSecret && containerRef.current) {
          checkoutRef.current = globalCheckoutInstance;
          try {
            if (!containerRef.current.hasChildNodes()) {
              globalCheckoutInstance.mount(containerRef.current);
              console.log('StripeEmbeddedCheckout: Mounted after waiting for initialization');
            }
          } catch (e) {
            console.warn('StripeEmbeddedCheckout: Could not mount after waiting', e);
          }
        }
      }).catch((err) => {
        console.warn('StripeEmbeddedCheckout: Initialization promise rejected', err);
        // If initialization failed, clear and try again
        clearGlobalStripeCheckout();
      });
      return;
    }

    // If initialization promise exists but for a different clientSecret, clear it
    if (initializationPromise && globalClientSecret !== clientSecret) {
      console.log('StripeEmbeddedCheckout: Clearing stale initialization promise');
      initializationPromise = null;
    }

    // Destroy any existing instance before creating a new one
    if (globalCheckoutInstance) {
      console.log('StripeEmbeddedCheckout: Destroying previous instance');
      try {
        globalCheckoutInstance.destroy();
      } catch (e) {
        console.warn('StripeEmbeddedCheckout: Error destroying previous instance', e);
      }
      globalCheckoutInstance = null;
      globalClientSecret = null;
    }

    if (checkoutRef.current) {
      try {
        checkoutRef.current.destroy();
      } catch (e) {
        // Ignore
      }
      checkoutRef.current = null;
    }

    console.log('StripeEmbeddedCheckout: Initializing with clientSecret:', clientSecret.substring(0, 50) + '...');
    globalClientSecret = clientSecret;

    let isMounted = true;

    // Create a promise for this initialization
    initializationPromise = (async () => {
      try {
        const stripe = await stripePromise;
        console.log('StripeEmbeddedCheckout: Stripe loaded');

        // Check if clientSecret has changed (more important than isMounted check)
        if (globalClientSecret !== clientSecret) {
          console.log('StripeEmbeddedCheckout: ClientSecret changed during initialization, aborting');
          return;
        }

        // Only check isMounted if we don't have a container (component might have unmounted)
        if (!isMounted && !containerRef.current) {
          console.log('StripeEmbeddedCheckout: Component unmounted and no container, aborting');
          return;
        }

        console.log('StripeEmbeddedCheckout: Creating embedded checkout...');
        const checkout = await stripe.initEmbeddedCheckout({
          clientSecret,
          onComplete: (result) => {
            console.log('StripeEmbeddedCheckout: onComplete called', result);
            if (completedRef.current) return;
            completedRef.current = true;
            
            // Clear global state after completion so next payment can start fresh
            setTimeout(() => {
              clearGlobalStripeCheckout();
            }, 100);
            
            onCompleteRef.current?.(result);
          }
        });

        // Final check - only abort if clientSecret changed (not just isMounted)
        if (globalClientSecret !== clientSecret) {
          console.log('StripeEmbeddedCheckout: ClientSecret changed before mount, destroying checkout');
          checkout.destroy();
          return;
        }

        // Check if we have a container to mount to
        if (!containerRef.current) {
          console.warn('StripeEmbeddedCheckout: Container ref is null, cannot mount');
          checkout.destroy();
          return;
        }

        console.log('StripeEmbeddedCheckout: Mounting checkout...');
        checkoutRef.current = checkout;
        globalCheckoutInstance = checkout;
        checkout.mount(containerRef.current);
        console.log('StripeEmbeddedCheckout: Checkout mounted successfully');
      } catch (error) {
        console.error('StripeEmbeddedCheckout: Error initializing checkout', error);
        clearGlobalStripeCheckout();
        throw error;
      } finally {
        initializationPromise = null;
      }
    })();

    return () => {
      isMounted = false;
      completedRef.current = false;
      
      // Only destroy if this component owns the instance
      if (checkoutRef.current && globalCheckoutInstance === checkoutRef.current) {
        try {
          checkoutRef.current.destroy();
        } catch (e) {
          // Ignore cleanup errors
        }
        checkoutRef.current = null;
        globalCheckoutInstance = null;
        globalClientSecret = null;
      }
    };
  }, [clientSecret]);

  return (
    <div
      ref={containerRef}
      id="stripe-embedded-container"
      style={{ width: '100%', minHeight: 520, borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' }}
    />
  );
}

